import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOperation, useOperation } from "../../../../../contexts/OperationContext";
import { getPlan } from '../../../../../contexts/PlanOperationContext/Plan';
import { datetime } from "../../../../../util/library/datetime";
import { selectPermissions } from '../../../../../util/store/selectors';
import { aDayMillisecond, OP_STATUS, SHORT_TIME_FORMAT, TIME_PER_STEP } from "../../../../../util/varibles/constants";
import { cloneObj, durationTxtByStartAndFinish } from "../../../../../util/varibles/global";
import { IActivityLog, IOperation } from "../../../../../util/varibles/interface";
import { ACTION } from '../../../../../util/varibles/permission';
import { planOpActions } from '../../../reducer';
import styles from '../style.module.scss';

interface IProps {
    start: number
    finish: number
    top: string
    activityLog: IActivityLog[]
    data: IOperation
}

export default function Timeline({ start, finish, top, activityLog, data }: IProps) {
    const dispatch = useDispatch();
    const [canUpdateOperation] = useSelector(selectPermissions([ACTION.PLAN_OP.UPDATE_OP]));
    const [canEditTimeline, setOperation] = useOperation(state => state.canEditTimeline);
    const funcRef = useRef({
        getPlan: getPlan(),
        get: getOperation()
    });
    const dragRef = useRef({
        targetX: 0,
        index: -1,
    });
    const duration = durationTxtByStartAndFinish(start, finish);
    const lastIndex = activityLog.length - 1;
    const { status, current_process = -1 } = data.operation;
    const { isOwn } = funcRef.current.get();
    const isEdit = isOwn && status !== OP_STATUS.FINISHED && canUpdateOperation && canEditTimeline;
    const points = [
        { name: 'start', label: 'Start', time: start, index: 0 },
        { name: 'finish', label: 'Complete', time: finish, index: lastIndex },
    ];

    const convertPxToTimeByStep = (value: number) => {
        const { calendarRef } = funcRef.current.getPlan();
        const el = calendarRef?.();
        if (!el)
            return 0;
        const dayWidth = Number(getComputedStyle(el).getPropertyValue('--date-per-screen').replace('px', '')) || 70;
        return Math.round(value / dayWidth * aDayMillisecond / TIME_PER_STEP) * TIME_PER_STEP;
    };

    const updateTime = (e: MouseEvent): IActivityLog[] => {
        const { index, targetX } = dragRef.current;
        const space = e.pageX - targetX;
        if (!space || index < 0) {
            return activityLog;
        }

        const duration = convertPxToTimeByStep(space);
        const max = activityLog.length - 1;

        if (index === 0) {
            return activityLog.map((item, i) => ({
                ...item,
                est_start_time: (duration < 0 && max === i) ? item.est_start_time : item.est_start_time + duration
            }));
        }

        const durationMax = (activityLog[index].est_start_time - activityLog[0].est_start_time) / 2;
        let rest = Math.max(duration, -durationMax), adjustment: Record<number, number> = {};
        for (let i = activityLog.length - 1; i > 0; i--) {
            if (i <= current_process) {
                break;
            } else if (i > index) {
                adjustment[i] = rest;
            } else {
                const time = activityLog[i].est_start_time - activityLog[i - 1].est_start_time;
                const limit = -time / 2;
                adjustment[i] = Math.max(time + limit, time + rest);
                rest -= limit;
                if (rest >= 0)
                    break;
            }
        }

        return activityLog.reduce((rs: IActivityLog[], item, i) => {
            if (!adjustment[i])
                return rs;
            const { est_start_time: prev } = rs[i - 1];
            if (i > index) {
                const { est_start_time: current } = rs[i];
                rs[i] = { ...item, est_start_time: Math.max(prev + TIME_PER_STEP, current + adjustment[i]) };
            } else {
                rs[i] = { ...item, est_start_time: prev + adjustment[i] };
            }
            return rs;
        }, cloneObj(activityLog));
    };

    const handleMouseMove = (e: MouseEvent) => {
        setOperation(prev => ({ activityLogs: { ...prev.activityLogs, [prev.rootKey]: updateTime(e) } }));
    };

    const handleMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        setOperation({ modeDrag: '' });
        const { updateOp } = funcRef.current.get();
        updateOp?.({ activity_log: updateTime(e) });
    };

    const handleMouseDown = (e: React.MouseEvent, index: number) => {
        const { real_start_time } = activityLog[index] || {};
        if (!isEdit || (index && index <= current_process) || real_start_time)
            return;

        e.preventDefault();
        e.stopPropagation();

        dragRef.current = {
            targetX: e.pageX,
            index,
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        setOperation({ modeDrag: 'time' });
        dispatch(planOpActions.setFocusId(data.operation.id));
    };

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        // Timeline drag listeners are registered imperatively by mouse handlers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        {points.map(item => (
            <div
                key={item.name}
                className={styles.timeline}
                data-name={item.name}
                data-edit={isEdit && (item.index > current_process || item.index === 0) && !activityLog[item.index]?.real_start_time}
                style={{ '--timeline-left': item.time, '--timeline-top': top } as React.CSSProperties}
            >
                <span className={styles.timelineLabel} onMouseDown={(e) => handleMouseDown(e, item.index)}>
                    {item.label}: {datetime(item.time).format(SHORT_TIME_FORMAT)}
                    {item.name === 'finish' && duration && <>
                        <span className={styles.timelineDivider} />
                        <span>{duration}</span>
                    </>}
                </span>
                <span className={styles.timelineLine} onMouseDown={(e) => handleMouseDown(e, item.index)} />
            </div>
        ))}
    </>
}
